/**
 * Dashboard Component
 * Main landing page for the Oath app with Agency Vitals, Trends, and Summary tabs
 * Professional layout with full space utilization
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowsRightLeftIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InboxArrowDownIcon,
  BuildingOffice2Icon,
  ClipboardDocumentListIcon,
  UserCircleIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useOathAuth } from '../../services/oathAuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { OathPageWrapper, OathSpinner, OathAgencySelector, OathModal } from '../shared/OathUI';
import { generateBsmsReport, BSMS_REPORTS } from '../../services/oathBsmsReportService';
import {
  getAgencyVitals,
  refreshAgencyVitals,
  getConsolidatedVitals,
  getSubAgencyAR,
  getStatusBadgeClass,
  getStatusTextClass,
  formatVitalsDate,
  formatVitalsDateTime,
  formatCurrency,
} from '../../services/oathAgencyVitalsService';
import { getReceiptPendingStatus } from '../../services/oathPowerReportService';
import { useOathNotifications } from '../../services/oathNotificationContext';
import { useTrends } from '../../hooks/useTrends';
import {
  QuickActionCard,
  StatItem,
  SummaryStat,
  VitalsSkeleton,
  VitalsError,
  AccountsReceivableTable,
  AgedOpenLiabilitySection,
} from './dashboard/components';
import TrendsTab from './dashboard/TrendsTab';
import SummaryTab from './dashboard/SummaryTab';

// ============================================
// Landing Page — shown when user lacks READ:DASHBOARD
// ============================================

const toTitleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const DashboardLandingPage = ({ user, quickLinks }) => {
  const firstName = toTitleCase(user?.firstName) || 'Agent';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const primaryAgency = user?.agencies?.[0];
  const agencyName = toTitleCase(primaryAgency?.dbaAgencyName || primaryAgency?.legalAgencyName || '');
  const gridCols = quickLinks.length <= 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3';

  return (
    <OathPageWrapper maxWidth={quickLinks.length <= 3 ? 'max-w-2xl' : 'max-w-4xl'} responsive fillHeight={false} className="!py-0 !sm:py-0">
      {/* Hero banner */}
      <div className="bg-white dark:bg-gray-800 rounded-b-2xl shadow-md border-b border-gray-200 dark:border-gray-700 -mx-4 sm:-mx-6 mb-6 sm:mb-8 overflow-hidden animate-fade-in-up">
        <div className="h-1 bg-gradient-to-r from-primary via-primary to-orange-500" />
        <div className="px-6 py-8 sm:py-10 text-center">
          <img src="/bankers-surety-logo.png" alt="Bankers Surety" className="h-6 sm:h-7 mx-auto mb-5 opacity-40 dark:opacity-30 dark:invert" />
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            {greeting}, {firstName}
          </h1>
          {agencyName && (
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-0.5">
              {agencyName}
            </p>
          )}
        </div>
      </div>

      {/* Cards */}
      <div className="px-2 sm:px-0 pb-8">
        {quickLinks.length > 0 ? (
          <div className={`grid grid-cols-1 ${gridCols} gap-4`}>
            {quickLinks.map(({ title, description, icon: Icon, path, variant }, i) => (
              <a
                key={path}
                href={path}
                className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8 text-center hover:shadow-xl hover:-translate-y-1 hover:border-primary/20 dark:hover:border-primary/30 transition-all duration-200 animate-fade-in-up overflow-hidden relative"
                style={{ animationDelay: `${100 + i * 75}ms` }}
              >
                <div className={`absolute top-0 left-0 right-0 h-0.5 transition-opacity duration-200 opacity-0 group-hover:opacity-100 ${
                  variant === 'orange' ? 'bg-orange-500' :
                  variant === 'green' ? 'bg-emerald-500' :
                  variant === 'red' ? 'bg-rose-500' :
                  variant === 'purple' ? 'bg-violet-500' :
                  'bg-primary'
                }`} />
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-200 group-hover:scale-110 group-hover:shadow-lg ${
                  variant === 'orange' ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 group-hover:shadow-orange-200/50 dark:group-hover:shadow-orange-900/30' :
                  variant === 'green' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 group-hover:shadow-emerald-200/50 dark:group-hover:shadow-emerald-900/30' :
                  variant === 'red' ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 group-hover:shadow-rose-200/50 dark:group-hover:shadow-rose-900/30' :
                  variant === 'purple' ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 group-hover:shadow-violet-200/50 dark:group-hover:shadow-violet-900/30' :
                  'bg-primary-50 dark:bg-primary-900/20 text-primary dark:text-primary-light group-hover:shadow-primary/20'
                }`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-primary dark:group-hover:text-primary-light transition-colors">
                  {title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  {description}
                </p>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 animate-fade-in-up">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No features are currently available.
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Contact your administrator for access.
            </p>
          </div>
        )}
      </div>
    </OathPageWrapper>
  );
};

// ============================================
// Dashboard — main component
// ============================================

const DASHBOARD_TABS = [
  { label: 'Vitals', icon: DocumentTextIcon },
  { label: 'Trends', icon: ChartBarIcon },
  { label: 'Summary', icon: ClipboardDocumentListIcon },
];
const TAB_KEYS = ['vitals', 'trends', 'summary'];

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useOathAuth();
  const {
    can,
    canAccessDashboard,
    canAccessTransferBonds,
    canAccessPowerInquiry,
    canAccessPowerReporting,
    canAccessForms,
    canAccessDischarges,
    canAccessReports,
    canAccessAdmin,
    canAccessSubAgencyAR,
    canAccessAR,
  } = usePermissions();
  const { notifications } = useOathNotifications();
  const notificationCount = notifications.filter((n) => n.contextTypeCode !== 'LB').length;

  // State
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [vitals, setVitals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [error, setError] = useState(null);
  const [isCached, setIsCached] = useState(false);
  const [pendingReceiptsCount, setPendingReceiptsCount] = useState(0);
  const [arReportModal, setArReportModal] = useState({ open: false, url: null, generating: false, showSubAgents: false });
  const loadingStartTime = useRef(null);
  const hasVitalsRef = useRef(false);

  // Tab state from URL search params (default to vitals)
  const activeTab = useMemo(() => {
    const tabParam = searchParams.get('view');
    const index = TAB_KEYS.indexOf(tabParam);
    return index >= 0 ? index : 0;
  }, [searchParams]);

  const handleTabChange = useCallback((index) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (index === 0) {
        next.delete('view');
      } else {
        next.set('view', TAB_KEYS[index]);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Sub-Agency AR state (GA only)
  const [subAgencyAR, setSubAgencyAR] = useState(null);
  const [subAgencyARLoading, setSubAgencyARLoading] = useState(false);

  // Minimum time to show loading overlay (prevents flicker)
  const MIN_LOADING_TIME = 400;

  // Get user's agencies (memoized to prevent useEffect dependency changes)
  const agencies = useMemo(() => user?.agencies || [], [user?.agencies]);

  // Build augmented agency list: insert "All (GA_ID)" before each General Agent agency
  const augmentedAgencies = useMemo(() => {
    const result = [];
    for (const agency of agencies) {
      if (agency.isGeneralAgent) {
        result.push({
          agencyId: `all-${agency.agencyId}`,
          dbaAgencyName: 'All',
          label: `All (${agency.agencyId})`,
          isConsolidated: true,
          sourceAgencyId: agency.agencyId,
        });
      }
      result.push(agency);
    }
    return result;
  }, [agencies]);

  const hasMultipleAgencies = augmentedAgencies.length > 1;

  // Parse consolidated selection — "all-26115" → { consolidated: true, agencyId: 26115 }
  const isConsolidatedSelection = typeof selectedAgencyId === 'string' && String(selectedAgencyId).startsWith('all-');
  const effectiveAgencyId = isConsolidatedSelection
    ? parseInt(String(selectedAgencyId).replace('all-', ''), 10)
    : selectedAgencyId;

  // Trends + Summary hooks - called once, shared between Trends tab and Summary tab
  const {
    trends, yoyChartData, denominationChartData, denominationKeys, historicalData,
    summary, summaryLoading, summaryError,
    loading: trendsLoading, error: trendsError, refetch: refetchTrends,
  } = useTrends({ agencyId: effectiveAgencyId, consolidated: isConsolidatedSelection });

  // Initialize selected agency - prefer main agency, then canPost, then first
  useEffect(() => {
    if (agencies.length > 0 && !selectedAgencyId) {
      const mainAgency = agencies.find(a => a.isMainAgency);
      const canPostAgency = agencies.find(a => a.canPost);
      const defaultAgency = mainAgency || canPostAgency || agencies[0];
      setSelectedAgencyId(defaultAgency.agencyId);
    }
  }, [agencies, selectedAgencyId]);

  // Fetch vitals data (supports consolidated mode for GA "All" selection)
  const fetchVitals = useCallback(async (rawAgencyId, forceRefresh = false) => {
    if (!rawAgencyId) return;

    const isConsolidated = typeof rawAgencyId === 'string' && String(rawAgencyId).startsWith('all-');
    const numericId = isConsolidated ? parseInt(String(rawAgencyId).replace('all-', ''), 10) : rawAgencyId;

    const isAgencySwitch = hasVitalsRef.current && !forceRefresh;

    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    if (isAgencySwitch) {
      setShowLoadingOverlay(true);
      loadingStartTime.current = Date.now();
    }

    setError(null);

    try {
      let result;
      if (isConsolidated) {
        result = await getConsolidatedVitals(numericId);
      } else if (forceRefresh) {
        result = await refreshAgencyVitals(numericId);
      } else {
        result = await getAgencyVitals(numericId);
      }

      if (result.success) {
        setVitals(result.data);
        hasVitalsRef.current = true;
        setIsCached(result.cached || false);
      } else {
        setError(result.error || 'Failed to load vitals');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Fetch vitals error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);

      if (isAgencySwitch && loadingStartTime.current) {
        const elapsed = Date.now() - loadingStartTime.current;
        const remaining = MIN_LOADING_TIME - elapsed;
        if (remaining > 0) {
          setTimeout(() => setShowLoadingOverlay(false), remaining);
        } else {
          setShowLoadingOverlay(false);
        }
      }
    }
  }, []);

  // Fetch vitals when agency changes
  useEffect(() => {
    if (selectedAgencyId) {
      fetchVitals(selectedAgencyId);
    }
  }, [selectedAgencyId, fetchVitals]);

  // Fetch sub-agency AR when agency changes (GA only — uses effectiveAgencyId for numeric ID)
  useEffect(() => {
    if (!effectiveAgencyId || !canAccessSubAgencyAR) {
      setSubAgencyAR(null);
      return;
    }
    let cancelled = false;
    setSubAgencyARLoading(true);
    getSubAgencyAR(effectiveAgencyId).then((result) => {
      if (cancelled) return;
      setSubAgencyARLoading(false);
      if (result.success && result.data?.isGeneralAgent) {
        setSubAgencyAR(result.data);
      } else {
        setSubAgencyAR(null);
      }
    });
    return () => { cancelled = true; };
  }, [effectiveAgencyId, canAccessSubAgencyAR]);

  // Fetch pending receipts count
  const fetchPendingReceipts = useCallback(async () => {
    try {
      const result = await getReceiptPendingStatus();
      if (result.success && result.data) {
        setPendingReceiptsCount(result.data.pendingCount || 0);
      }
    } catch (err) {
      console.error('Failed to fetch pending receipts:', err);
    }
  }, []);

  useEffect(() => {
    if (canAccessPowerReporting) {
      fetchPendingReceipts();
    }
  }, [canAccessPowerReporting, fetchPendingReceipts]);

  // Handle agency change
  const handleAgencyChange = (agencyId) => {
    setSelectedAgencyId(agencyId);
  };

  // Handle refresh
  const handleRefresh = () => {
    if (selectedAgencyId && !refreshing) {
      fetchVitals(selectedAgencyId, true);
      refetchTrends();
      if (canAccessPowerReporting) {
        fetchPendingReceipts();
      }
    }
  };

  // Generate AR statement report inline
  const generateArReport = useCallback((showSubAgents) => {
    // Revoke previous blob URL
    setArReportModal((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { open: true, url: null, generating: true, showSubAgents };
    });
    generateBsmsReport({
      ...BSMS_REPORTS.AGENT_STATEMENT_PACKAGE,
      additionalParams: { agentId: String(effectiveAgencyId), showSubAgents: showSubAgents ? 'Y' : 'N' },
    }).then((result) => {
      if (result.success && result.data) {
        const url = URL.createObjectURL(result.data);
        setArReportModal((prev) => ({ ...prev, url, generating: false }));
      } else {
        setArReportModal({ open: false, url: null, generating: false, showSubAgents: false });
        toast.error(result.error || 'Failed to generate statement');
      }
    });
  }, [effectiveAgencyId]);

  // Handle drill-down navigation (uses effectiveAgencyId for numeric ID)
  const handleDrillDown = (category, item) => {
    if (category === 'aged-open-liability') {
      const params = new URLSearchParams();
      params.set('groupFilter', 'OpenLiability');
      if (effectiveAgencyId) {
        params.set('agencyIds', effectiveAgencyId.toString());
      }

      // Backend AOL bucket filters handle date ranges internally —
      // no need for dateType/startDate/endDate on the frontend.
      if (item.filterName) {
        params.set('specificFilters', item.filterName);
      }

      navigate(`/app/power-inquiry?${params.toString()}`);
      return;
    }

    if (category === 'accounts-receivable') {
      generateArReport(isConsolidatedSelection);
      return;
    }

    const encodedCategory = encodeURIComponent(category);
    const encodedItem = encodeURIComponent(item);
    navigate(`/app/vitals/${encodedCategory}/${encodedItem}?agencyId=${effectiveAgencyId}`);
  };

  // Mapping from dashboard stat keys to Power Inquiry filters
  const STAT_TO_FILTER_MAP = {
    'open-liability': { groupFilter: 'OpenLiability' },
    'open-judgments': { specificFilters: ['Judgment'] },
    'open-forfeitures': { specificFilters: ['Forfeiture', 'UnexecutedForfeiture'] },
    'expired-powers': { specificFilters: ['Expired'] },
    'missing-powers': { specificFilters: ['Missing'] },
    'discharged-powers': { specificFilters: ['RecentDischarges'] },
    'unexecuted-powers': { specificFilters: ['Unexecuted'] },
    'unexecuted-judgments': { specificFilters: ['UnexecutedJudgments'] },
    'unexecuted-forfeitures': { specificFilters: ['UnexecutedForfeiture'] },
    'unexecuted-discharges': { specificFilters: ['UnexecutedDischarge'] },
  };

  const createStatClickHandler = (statKey) => () => {
    // Last Report → navigate to Power Inquiry searching by report number in report view
    if (statKey === 'last-report' && vitals?.lastReportNumber) {
      const params = new URLSearchParams();
      params.set('reportNo', vitals.lastReportNumber);
      params.set('viewMode', 'byReport');
      params.set('expandedReport', vitals.lastReportNumber);
      if (effectiveAgencyId) {
        params.set('agencyIds', effectiveAgencyId.toString());
      }
      navigate(`/app/power-inquiry?${params.toString()}`);
      return;
    }

    const filterConfig = STAT_TO_FILTER_MAP[statKey];
    if (filterConfig) {
      const params = new URLSearchParams();
      if (filterConfig.groupFilter) {
        params.set('groupFilter', filterConfig.groupFilter);
      }
      if (filterConfig.specificFilters?.length > 0) {
        params.set('specificFilters', filterConfig.specificFilters.join(','));
      }
      if (effectiveAgencyId) {
        params.set('agencyIds', effectiveAgencyId.toString());
      }
      navigate(`/app/power-inquiry?${params.toString()}`);
    } else {
      handleDrillDown('snapshot', statKey);
    }
  };

  // Priority-ordered quick action queue — first 4 visible cards are shown
  const quickActions = [
    {
      title: 'Power Inquiry',
      description: 'Search and view power details',
      icon: MagnifyingGlassIcon,
      path: '/app/power-inquiry',
      variant: 'orange',
      show: canAccessPowerInquiry,
    },
    {
      title: 'Power Reporting',
      description: 'Track and submit in real-time',
      icon: ChartBarIcon,
      path: '/app/power-reports',
      variant: 'green',
      show: canAccessPowerReporting,
    },
    {
      title: 'Power Receipts',
      description: pendingReceiptsCount > 0 ? `${pendingReceiptsCount} pending to confirm` : 'Confirm power shipments',
      icon: InboxArrowDownIcon,
      path: '/app/power-receipts',
      variant: 'red',
      show: canAccessPowerInquiry,
      badgeCount: pendingReceiptsCount,
    },
    {
      title: 'Agency Manager',
      description: 'Manage agencies and users',
      icon: Cog6ToothIcon,
      path: '/app/admin',
      variant: 'purple',
      show: canAccessAdmin,
    },
    // Backfill cards — fill empty slots in priority order
    {
      title: 'Transfer Bonds',
      description: notificationCount > 0 ? `${notificationCount} pending notification${notificationCount !== 1 ? 's' : ''}` : 'Request and post bonds',
      icon: ArrowsRightLeftIcon,
      path: '/app/transfer-bonds',
      variant: 'orange',
      show: canAccessTransferBonds,
      badgeCount: notificationCount,
    },
    {
      title: 'Discharges',
      description: 'Manage bond discharges',
      icon: DocumentTextIcon,
      path: '/app/discharges',
      variant: 'red',
      show: canAccessDischarges,
    },
    {
      title: 'Forms',
      description: 'Browse and download forms',
      icon: ClipboardDocumentListIcon,
      path: '/app/forms',
      variant: 'purple',
      show: canAccessForms,
    },
    {
      title: 'Reports',
      description: 'Access report center',
      icon: ChartBarIcon,
      path: '/app/reports',
      variant: 'green',
      show: canAccessReports,
    },
    {
      title: 'Power Log',
      description: 'View power execution history',
      icon: ClockIcon,
      path: '/app/power-log',
      variant: 'orange',
      show: can('READ:POWER-LOG'),
    },
    {
      title: 'My Profile',
      description: 'View and update your account',
      icon: UserCircleIcon,
      path: '/app/profile',
      variant: 'primary',
      show: true,
    },
  ];

  const visibleActions = quickActions.filter((action) => action.show).slice(0, 4);

  // Helper functions for metric display
  const getMetricDisplay = (metric, fallback = '-') => metric?.displayValue || fallback;
  const getMetricCount = (metric) => metric?.count;
  const getMetricStatus = (metric) => metric?.status || 'normal';
  const getMetricTooltip = (metric) => metric?.tooltip;

  // Get selected agency name (supports consolidated "All" entries)
  const selectedAgency = augmentedAgencies.find((a) => a.agencyId === selectedAgencyId);
  const isSelectedAgencyGA = selectedAgency?.isGeneralAgent || selectedAgency?.isConsolidated || agencies.find((a) => a.agencyId === effectiveAgencyId)?.isGeneralAgent || false;
  const agencyDisplayName = selectedAgency?.label || selectedAgency?.dbaAgencyName || selectedAgency?.legalAgencyName || 'Agency Portal';
  const agencyName = selectedAgency?.label ? selectedAgency.label : (selectedAgency ? `${agencyDisplayName} (${selectedAgency.agencyId})` : agencyDisplayName);

  // Landing page links — used when user lacks READ:DASHBOARD
  const canAccessLargeBonds = can('CREATE:LARGE-BOND:REQUEST');
  const landingLinks = useMemo(() => [
    { title: 'Power Inquiry', description: 'Search and view power details', icon: MagnifyingGlassIcon, path: '/app/power-inquiry', variant: 'orange', show: canAccessPowerInquiry },
    { title: 'Power Reporting', description: 'Track and submit in real-time', icon: ChartBarIcon, path: '/app/power-reports', variant: 'green', show: canAccessPowerReporting },
    { title: 'Transfer Bonds', description: 'Request and post bonds', icon: ArrowsRightLeftIcon, path: '/app/transfer-bonds', variant: 'orange', show: canAccessTransferBonds },
    { title: 'Large Bonds', description: 'Submit large bond requests', icon: BanknotesIcon, path: '/app/large-bonds', variant: 'primary', show: canAccessLargeBonds },
    { title: 'Discharges', description: 'Manage bond discharges', icon: DocumentTextIcon, path: '/app/discharges', variant: 'red', show: canAccessDischarges },
    { title: 'Forms', description: 'Browse and download forms', icon: ClipboardDocumentListIcon, path: '/app/forms', variant: 'purple', show: canAccessForms },
    { title: 'Reports', description: 'Access report center', icon: ChartBarIcon, path: '/app/reports', variant: 'green', show: canAccessReports },
    { title: 'Agency Manager', description: 'Manage agencies and users', icon: Cog6ToothIcon, path: '/app/admin', variant: 'purple', show: canAccessAdmin },
  ].filter((link) => link.show), [canAccessPowerInquiry, canAccessPowerReporting, canAccessTransferBonds, canAccessLargeBonds, canAccessDischarges, canAccessForms, canAccessReports, canAccessAdmin]);

  // If user lacks READ:DASHBOARD, show branded landing page
  if (!canAccessDashboard) {
    return <DashboardLandingPage user={user} quickLinks={landingLinks} />;
  }

  return (
    <>
    <OathPageWrapper maxWidth="max-w-7xl" responsive fillHeight={false} className="!py-2 sm:!py-3 qhd:!py-6">
      {/* Loading overlay when switching agencies */}
      {(showLoadingOverlay || refreshing) && vitals && (
        <div className="fixed inset-x-0 top-12 sm:top-14 bottom-0 bg-primary/10 dark:bg-primary/20 z-40 flex items-center justify-center backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2 qhd:gap-3 bg-white dark:bg-gray-800 px-6 py-4 qhd:px-8 qhd:py-5 4k:px-10 4k:py-6 rounded-xl qhd:rounded-2xl shadow-lg border border-primary/20">
            <OathSpinner size="md" />
            <span className="text-sm qhd:text-base 4k:text-lg text-primary dark:text-primary-light font-medium">
              {refreshing ? 'Refreshing...' : 'Loading agency data...'}
            </span>
          </div>
        </div>
      )}
      {loading && !vitals ? (
        <VitalsSkeleton />
      ) : error && !vitals ? (
        <VitalsError error={error} onRetry={() => fetchVitals(selectedAgencyId)} />
      ) : (
        <div className="space-y-3 qhd:space-y-6 4k:space-y-8">
          {/* Welcome Header - Full Width (always visible above tabs) */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl qhd:rounded-3xl shadow-xl overflow-hidden">
            <div className="h-1.5 qhd:h-2 4k:h-2.5 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400" />
            <div className="p-3 sm:p-4 qhd:p-6 4k:p-8">
              {/* Mobile Layout */}
              <div className="sm:hidden space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/30">
                    <span className="text-base font-bold text-white">
                      {user?.firstName?.charAt(0) || 'A'}
                    </span>
                  </div>
                  <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                    Welcome, {user?.firstName || 'Agent'}!
                  </h1>
                </div>
                {hasMultipleAgencies ? (
                  <OathAgencySelector agencies={augmentedAgencies} selectedAgencyId={selectedAgencyId} onChange={handleAgencyChange} className="w-full block" size="sm" />
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{agencyName}</p>
                )}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-2 space-y-2">
                  <SummaryStat label="Open Liability" value={getMetricDisplay(vitals?.openLiability, '$0')} status={getMetricStatus(vitals?.openLiability)} />
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                    <SummaryStat label="Forfeitures" value={getMetricCount(vitals?.openForfeitures) ?? 0} status={getMetricStatus(vitals?.openForfeitures)} />
                    <SummaryStat label="Judgments" value={getMetricCount(vitals?.openJudgments) ?? 0} status={getMetricStatus(vitals?.openJudgments)} />
                  </div>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors disabled:opacity-50"
                  title="Click to get the latest data"
                >
                  <ArrowPathIcon className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    {refreshing ? 'Refreshing...' : vitals?.lastUpdated ? `Updated ${formatVitalsDateTime(vitals.lastUpdated)}` : 'Refresh'}
                  </span>
                </button>
              </div>

              {/* Desktop Layout */}
              <div className="hidden sm:flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 qhd:gap-6">
                <div className="flex items-center gap-3 qhd:gap-5 4k:gap-6">
                  <div className="w-10 h-10 qhd:w-14 qhd:h-14 4k:w-16 4k:h-16 bg-gradient-to-br from-primary to-primary-dark rounded-xl qhd:rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/30">
                    <span className="text-base qhd:text-xl 4k:text-2xl font-bold text-white">
                      {user?.firstName?.charAt(0) || 'A'}
                    </span>
                  </div>
                  <div>
                    <h1 className="text-lg qhd:text-2xl 4k:text-3xl font-bold text-gray-900 dark:text-white">
                      Welcome back, {user?.firstName || 'Agent'}!
                    </h1>
                    <div className="flex items-center gap-2">
                      {hasMultipleAgencies ? (
                        <OathAgencySelector agencies={augmentedAgencies} selectedAgencyId={selectedAgencyId} onChange={handleAgencyChange} className="mt-1" size="sm" />
                      ) : (
                        <p className="text-sm qhd:text-base 4k:text-lg text-gray-500 dark:text-gray-400">{agencyName}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center divide-x divide-gray-200 dark:divide-gray-600 bg-gray-50 dark:bg-gray-700/50 rounded-xl qhd:rounded-2xl px-2 py-2 qhd:px-3 qhd:py-3 4k:px-4 4k:py-4">
                  <SummaryStat label="Open Liability" value={getMetricDisplay(vitals?.openLiability, '$0')} status={getMetricStatus(vitals?.openLiability)} />
                  <SummaryStat label="Open Forfeitures" value={getMetricCount(vitals?.openForfeitures) ?? 0} status={getMetricStatus(vitals?.openForfeitures)} />
                  <SummaryStat label="Open Judgments" value={getMetricCount(vitals?.openJudgments) ?? 0} status={getMetricStatus(vitals?.openJudgments)} />
                  <div className="pl-3 qhd:pl-4 4k:pl-5">
                    <button
                      onClick={handleRefresh}
                      disabled={refreshing}
                      className="flex items-center gap-1.5 qhd:gap-2 px-2.5 py-1 qhd:px-3 qhd:py-1.5 4k:px-4 4k:py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-primary/40 dark:hover:border-primary/50 hover:bg-primary-50 dark:hover:bg-primary-900/20 text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary-light transition-all disabled:opacity-50 disabled:hover:bg-white dark:disabled:hover:bg-gray-700 disabled:hover:border-gray-200 dark:disabled:hover:border-gray-600 disabled:hover:text-gray-500"
                      title={vitals?.lastUpdated ? `Last updated ${formatVitalsDateTime(vitals.lastUpdated)}` : 'Refresh vitals'}
                    >
                      <ArrowPathIcon className={`w-3.5 h-3.5 qhd:w-4 qhd:h-4 4k:w-5 4k:h-5 ${refreshing ? 'animate-spin' : ''}`} />
                      {vitals?.lastUpdated && (
                        <span className="text-[10px] qhd:text-xs 4k:text-sm whitespace-nowrap">
                          {formatVitalsDateTime(vitals.lastUpdated)}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* View navigation — underline indicator style, inside the header card */}
            <div className="flex items-center border-t border-gray-100 dark:border-gray-700">
              {DASHBOARD_TABS.map(({ label, icon: Icon }, idx) => (
                <button
                  key={label}
                  onClick={() => handleTabChange(idx)}
                  className={`relative flex items-center justify-center gap-1.5 qhd:gap-2 px-4 py-2 qhd:px-6 qhd:py-2.5 4k:px-8 4k:py-3 text-xs qhd:text-sm 4k:text-base font-medium transition-colors ${
                    activeTab === idx
                      ? 'text-primary dark:text-primary-light'
                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 qhd:w-4 qhd:h-4 4k:w-5 4k:h-5" />
                  <span>{label}</span>
                  {/* Active indicator */}
                  {activeTab === idx && (
                    <span className="absolute bottom-0 left-4 right-4 qhd:left-6 qhd:right-6 h-0.5 bg-primary dark:bg-primary-light rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Quick Actions - Horizontal Grid (always visible above tabs) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 qhd:gap-4 4k:gap-6">
            {visibleActions.map((action) => (
              <QuickActionCard
                key={action.path}
                title={action.title}
                description={action.description}
                icon={action.icon}
                variant={action.variant}
                onClick={() => navigate(action.path)}
                badgeCount={action.badgeCount || 0}
              />
            ))}
          </div>

          {/* Tab Content — driven by activeTab state, toggled from Welcome Header */}
          {activeTab === 0 && (
                  <div className="space-y-3 qhd:space-y-6 4k:space-y-8">
                    {/* Data Cards Grid - 2 columns when AR visible, full width otherwise */}
                    <div className={`grid grid-cols-1 ${canAccessAR ? 'lg:grid-cols-2' : ''} gap-3 qhd:gap-6 4k:gap-8`}>
                      {/* Current Snapshot */}
                      <div className="bg-gray-50 dark:bg-gray-700/30 rounded-2xl qhd:rounded-3xl overflow-hidden border border-gray-200 dark:border-gray-700">
                        <div className="h-1 qhd:h-1.5 4k:h-2 bg-gradient-to-r from-primary to-primary-dark" />
                        <div className="px-4 py-2 qhd:px-5 qhd:py-3 4k:px-6 4k:py-4 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-2 qhd:gap-3">
                            <DocumentTextIcon className="w-4 h-4 qhd:w-5 qhd:h-5 4k:w-6 4k:h-6 text-primary dark:text-primary-light" />
                            <h2 className="text-sm qhd:text-base 4k:text-lg font-semibold text-gray-900 dark:text-white">Current Snapshot</h2>
                            {refreshing && <OathSpinner size="sm" />}
                          </div>
                        </div>
                        <div className="p-3 qhd:p-5 4k:p-6">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 qhd:gap-x-8 4k:gap-x-10">
                            <div>
                              <StatItem label="Open Liability" count={getMetricCount(vitals?.openLiability)} value={getMetricDisplay(vitals?.openLiability)} status={getMetricStatus(vitals?.openLiability)} tooltip={getMetricTooltip(vitals?.openLiability)} onClick={createStatClickHandler('open-liability')} />
                              <StatItem label="Open Judgments" count={getMetricCount(vitals?.openJudgments)} value={getMetricDisplay(vitals?.openJudgments)} status={getMetricStatus(vitals?.openJudgments)} tooltip={getMetricTooltip(vitals?.openJudgments)} onClick={createStatClickHandler('open-judgments')} />
                              <StatItem label="Open Forfeitures" count={getMetricCount(vitals?.openForfeitures)} value={getMetricDisplay(vitals?.openForfeitures)} status={getMetricStatus(vitals?.openForfeitures)} tooltip={getMetricTooltip(vitals?.openForfeitures)} onClick={createStatClickHandler('open-forfeitures')} />
                              <StatItem label="Last Report" count={formatVitalsDate(vitals?.lastReportDate)} status="normal" tooltip={vitals?.lastReportNumber ? `Report: ${vitals.lastReportNumber}` : undefined} onClick={createStatClickHandler('last-report')} />
                              <StatItem label="6-Month Turnaround" count={getMetricDisplay(vitals?.turnaround, '-')} status={getMetricStatus(vitals?.turnaround)} tooltip={getMetricTooltip(vitals?.turnaround)} />
                              <StatItem label="Expired Powers" count={getMetricCount(vitals?.expiredPowers) ?? getMetricDisplay(vitals?.expiredPowers, '0')} status={vitals?.expiredPowers?.count > 0 ? 'critical' : getMetricStatus(vitals?.expiredPowers)} tooltip={getMetricTooltip(vitals?.expiredPowers)} onClick={createStatClickHandler('expired-powers')} />
                            </div>
                            <div>
                              <StatItem label="Recent Discharges" count={getMetricCount(vitals?.dischargedPowers)} value={getMetricDisplay(vitals?.dischargedPowers)} status={getMetricStatus(vitals?.dischargedPowers)} tooltip={getMetricTooltip(vitals?.dischargedPowers)} onClick={createStatClickHandler('discharged-powers')} />
                              <StatItem label="Unexecuted Powers" count={getMetricCount(vitals?.unexecutedPowers)} value={getMetricDisplay(vitals?.unexecutedPowers)} status={getMetricStatus(vitals?.unexecutedPowers)} tooltip={getMetricTooltip(vitals?.unexecutedPowers)} onClick={createStatClickHandler('unexecuted-powers')} />
                              <StatItem label="Unexecuted Judgments" count={getMetricCount(vitals?.unexecutedJudgments)} value={getMetricDisplay(vitals?.unexecutedJudgments)} status={getMetricStatus(vitals?.unexecutedJudgments)} tooltip={getMetricTooltip(vitals?.unexecutedJudgments)} onClick={createStatClickHandler('unexecuted-judgments')} />
                              <StatItem label="Unexecuted Forfeitures" count={getMetricCount(vitals?.unexecutedForfeitures)} value={getMetricDisplay(vitals?.unexecutedForfeitures)} status={getMetricStatus(vitals?.unexecutedForfeitures)} tooltip={getMetricTooltip(vitals?.unexecutedForfeitures)} onClick={createStatClickHandler('unexecuted-forfeitures')} />
                              <StatItem label="Unexecuted Discharges" count={getMetricCount(vitals?.unexecutedDischarges)} value={getMetricDisplay(vitals?.unexecutedDischarges)} status={getMetricStatus(vitals?.unexecutedDischarges)} tooltip={getMetricTooltip(vitals?.unexecutedDischarges)} onClick={createStatClickHandler('unexecuted-discharges')} />
                              <StatItem label="Missing Powers" count={getMetricCount(vitals?.missingPowers) ?? getMetricDisplay(vitals?.missingPowers, '0')} status={getMetricStatus(vitals?.missingPowers)} tooltip={getMetricTooltip(vitals?.missingPowers)} onClick={createStatClickHandler('missing-powers')} />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Accounts Receivable — gated by READ:AR */}
                      {canAccessAR && <div className="bg-gray-50 dark:bg-gray-700/30 rounded-2xl qhd:rounded-3xl overflow-hidden border border-gray-200 dark:border-gray-700">
                        <div className="h-1 qhd:h-1.5 4k:h-2 bg-gradient-to-r from-orange-400 to-orange-500" />
                        <div className="px-4 py-2 qhd:px-5 qhd:py-3 4k:px-6 4k:py-4 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-2 qhd:gap-3">
                            <BanknotesIcon className="w-4 h-4 qhd:w-5 qhd:h-5 4k:w-6 4k:h-6 text-orange-500 dark:text-orange-400" />
                            <h2 className="text-sm qhd:text-base 4k:text-lg font-semibold text-gray-900 dark:text-white">Accounts Receivable</h2>
                            {refreshing && <OathSpinner size="sm" />}
                          </div>
                        </div>
                        <AccountsReceivableTable data={vitals?.accountsReceivable} onRowClick={handleDrillDown} />

                        {/* Sub-Agency A/R — inline footer (GA only) */}
                        {canAccessSubAgencyAR && (subAgencyARLoading || subAgencyAR) && (
                          <button
                            onClick={() => navigate('/app/sub-agency-ar')}
                            className="w-full border-t-2 border-blue-200 dark:border-blue-800/60 px-3 py-2 sm:px-4 sm:py-1.5 qhd:px-5 qhd:py-2 4k:px-6 4k:py-2.5 bg-blue-50/70 dark:bg-blue-900/20 hover:bg-blue-100/80 dark:hover:bg-blue-900/30 transition-colors group"
                          >
                            {/* Header row — always visible */}
                            <div className="flex items-center justify-between gap-2 sm:hidden mb-1.5">
                              <div className="flex items-center gap-1.5">
                                <BuildingOffice2Icon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                <span className="text-[10px] font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide">Sub-Agencies</span>
                                {subAgencyARLoading && <div className="w-3 h-3 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" />}
                              </div>
                              <ChevronRightIcon className="h-3 w-3 text-blue-400 group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors" />
                            </div>

                            {/* Mobile: 2x2 grid of pills */}
                            {!subAgencyARLoading && subAgencyAR && (() => {
                              const ar = subAgencyAR.totals?.accountsReceivable || [];
                              const buckets = [
                                { key: 'Current', label: 'Current', cls: 'text-green-700 dark:text-green-400', bgCls: 'bg-green-100 dark:bg-green-900/30' },
                                { key: '30-60 Days', label: '30-60 Days', cls: 'text-yellow-700 dark:text-yellow-400', bgCls: 'bg-yellow-100 dark:bg-yellow-900/30' },
                                { key: '60-90 Days', label: '60-90 Days', cls: 'text-orange-700 dark:text-orange-400', bgCls: 'bg-orange-100 dark:bg-orange-900/30' },
                                { key: '90+ Days', label: '90+ Days', cls: 'text-red-700 dark:text-red-400', bgCls: 'bg-red-100 dark:bg-red-900/30' },
                              ];
                              const over90 = ar.find((i) => i.description === '90+ Days');
                              const hasOver90 = over90?.net > 0;
                              return (
                                <>
                                  <div className="grid grid-cols-2 gap-1.5 sm:hidden">
                                    {buckets.map((bucket) => {
                                      const item = ar.find((i) => i.description === bucket.key);
                                      const isAlert = bucket.key === '90+ Days' && hasOver90;
                                      return (
                                        <div key={bucket.key} className={`flex items-center justify-between gap-1 px-2 py-1 rounded ${bucket.bgCls}`}>
                                          <span className="flex items-center gap-1 min-w-0">
                                            {isAlert && <ExclamationTriangleIcon className="h-3 w-3 text-red-600 dark:text-red-400 flex-shrink-0" />}
                                            <span className={`text-[10px] font-semibold ${bucket.cls} truncate`}>{bucket.label}</span>
                                          </span>
                                          <span className={`text-[10px] font-bold tabular-nums flex-shrink-0 ${isAlert ? 'text-red-800 dark:text-red-300' : 'text-gray-900 dark:text-white'}`}>
                                            {item?.totalDisplay || formatCurrency(item?.total ?? 0)}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Desktop layout: single row with pills */}
                                  <div className="hidden sm:flex items-center gap-2.5 qhd:gap-3">
                                    <BuildingOffice2Icon className="w-3.5 h-3.5 qhd:w-4 qhd:h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                    <span className="text-[10px] qhd:text-xs font-bold text-blue-700 dark:text-blue-300 flex-shrink-0 uppercase tracking-wide">Sub-Agencies</span>
                                    <span className="w-px h-3 bg-blue-200 dark:bg-blue-700 flex-shrink-0" />
                                    <div className="flex items-center gap-1.5 qhd:gap-2 flex-1 min-w-0">
                                      {buckets.map((bucket) => {
                                        const item = ar.find((i) => i.description === bucket.key);
                                        const isAlert = bucket.key === '90+ Days' && hasOver90;
                                        return (
                                          <span key={bucket.key} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${bucket.bgCls} flex-shrink-0`}>
                                            {isAlert && <ExclamationTriangleIcon className="h-3 w-3 text-red-600 dark:text-red-400" />}
                                            <span className={`text-[10px] qhd:text-xs font-semibold ${bucket.cls}`}>{bucket.label}</span>
                                            <span className={`text-[10px] qhd:text-xs font-bold tabular-nums ${isAlert ? 'text-red-800 dark:text-red-300' : 'text-gray-900 dark:text-white'}`}>
                                              {item?.totalDisplay || formatCurrency(item?.total ?? 0)}
                                            </span>
                                          </span>
                                        );
                                      })}
                                    </div>
                                    <ChevronRightIcon className="h-3 w-3 qhd:h-3.5 qhd:w-3.5 text-blue-400 dark:text-blue-500 group-hover:text-blue-600 dark:group-hover:text-blue-300 flex-shrink-0 transition-colors" />
                                  </div>
                                </>
                              );
                            })()}

                            {subAgencyARLoading && (
                              <div className="hidden sm:flex items-center gap-2.5">
                                <BuildingOffice2Icon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                <span className="text-[10px] qhd:text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide">Sub-Agencies</span>
                                <div className="flex-1 h-3 animate-pulse bg-blue-200/60 dark:bg-blue-700/40 rounded" />
                              </div>
                            )}
                          </button>
                        )}
                      </div>}
                    </div>

                    {/* Aged Open Liability - Full Width */}
                    <div className="bg-gray-50 dark:bg-gray-700/30 rounded-2xl qhd:rounded-3xl overflow-hidden border border-gray-200 dark:border-gray-700">
                      <div className="h-1 qhd:h-1.5 4k:h-2 bg-gradient-to-r from-green-500 to-green-600" />
                      <div className="px-4 py-2 qhd:px-5 qhd:py-3 4k:px-6 4k:py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2 qhd:gap-3">
                          <ClockIcon className="w-4 h-4 qhd:w-5 qhd:h-5 4k:w-6 4k:h-6 text-green-600 dark:text-green-400" />
                          <h2 className="text-sm qhd:text-base 4k:text-lg font-semibold text-gray-900 dark:text-white">Aged Open Liability</h2>
                          {refreshing && <OathSpinner size="sm" />}
                        </div>
                      </div>
                      <AgedOpenLiabilitySection data={vitals?.agedOpenLiability} onBucketClick={handleDrillDown} />
                    </div>
                  </div>
          )}

          {activeTab === 1 && (
            <TrendsTab
              yoyChartData={yoyChartData}
              denominationChartData={denominationChartData}
              denominationKeys={denominationKeys}
              historicalData={historicalData}
              loading={trendsLoading}
              error={trendsError}
              onRetry={refetchTrends}
            />
          )}

          {activeTab === 2 && (
            <SummaryTab
              summary={summary}
              summaryLoading={summaryLoading}
              summaryError={summaryError}
            />
          )}
        </div>
      )}
    </OathPageWrapper>

    {/* AR Statement Modal */}
    <OathModal
      isOpen={arReportModal.open}
      onClose={() => {
        if (arReportModal.url) URL.revokeObjectURL(arReportModal.url);
        setArReportModal({ open: false, url: null, generating: false, showSubAgents: false });
      }}
      title="Agent Statement Package"
      size="xl"
      footer={isSelectedAgencyGA ? (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <BuildingOffice2Icon className="h-4 w-4" />
            <span>{arReportModal.showSubAgents ? `${agencyDisplayName} + sub-agencies` : `${agencyDisplayName} only`}</span>
          </div>
          <button
            type="button"
            onClick={() => generateArReport(!arReportModal.showSubAgents)}
            disabled={arReportModal.generating}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
              arReportModal.showSubAgents
                ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                : 'border-primary/30 dark:border-primary/40 bg-primary-50 dark:bg-primary-900/20 text-primary dark:text-primary-light hover:bg-primary-100 dark:hover:bg-primary-900/30'
            }`}
          >
            <BuildingOffice2Icon className="h-4 w-4" />
            {arReportModal.showSubAgents ? 'Exclude Sub-Agencies' : 'Include Sub-Agencies'}
          </button>
        </div>
      ) : null}
    >
      <div className="w-full h-[60vh] sm:h-[70vh]">
        {arReportModal.generating ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <OathSpinner size="lg" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Generating report...</p>
          </div>
        ) : arReportModal.url ? (
          <iframe
            src={arReportModal.url}
            title="Agent Statement Package"
            className="w-full h-full border-0 rounded-lg"
          />
        ) : null}
      </div>
    </OathModal>
    </>
  );
};

export default Dashboard;
